import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderService, OrderDTO } from 'ticket-sdk';
import { of, Subject } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { environment } from '@env';
import { NavigationService } from '@app/core/services/navigation.service';
import { Platform, ToastController } from '@ionic/angular';
import { Share } from '@capacitor/share';

declare let cordova: any;

@Component({
  selector: 'app-receipt',
  templateUrl: './receipt.page.html',
  styleUrls: ['./receipt.page.scss'],
})
export class ReceiptPage implements OnInit, OnDestroy {
  canGoBack: boolean = false;
  externalOrderId: any;
  order: OrderDTO;
  sharingSupported: boolean;
  total: number = 0;
  totalVAT: number = 0;
  
  private onDestroy = new Subject();

  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private orderService: OrderService,
    private navigationService: NavigationService,
    public toastController: ToastController,
    private router: Router
  ) { }

  get isProd(): boolean {
    return environment.production;
  }

  get pdfUrl(): string {
    return this.order ? `${environment.ticketServerUrl}/api/v1/public/orders/${this.order.orderId}/receipt` : '';
  }

  ngOnInit() {
    this.externalOrderId = this.route.snapshot.params.externalOrderId;

    this.orderService.getOrderByExternalId(this.externalOrderId)
    .pipe(
      take(1),
      catchError(async error => {
        const toast = await this.toastController.create({
          message: 'Ogiltigt order-ID.',
          color: 'danger',
          position: 'top',
          duration: 3000
        });
        toast.present();
        this.router.navigateByUrl('buy-ticket');
        return of(null);
      })
    )
    .subscribe((order: OrderDTO) => {
      this.order = order;
      this.calculateSum();
    });

    Share.canShare().then(canShare => this.sharingSupported = canShare.value);
  }

  ionViewWillEnter() {
    this.canGoBack = this.navigationService.getCanGoBack();
  }

  calculateSum() {
    let total = 0;
    let totalVAT = 0;
    this.order?.orderItems?.forEach(orderItem => {
      total += orderItem.price;
      totalVAT += orderItem.price - orderItem.price / (1 + orderItem.vat);
    });
    this.total = total;
    this.totalVAT = totalVAT;
  }

  getPdfReceipt() {
    if (!!this.order) {
      if (this.platform.is('capacitor')) {
        let browserOptions: string = '';
        browserOptions += 'closebuttoncaption=Stäng,';
        browserOptions += 'toolbarposition=top,';
        browserOptions += 'toolbartranslucent=no,';
        browserOptions += 'hidenavigationbuttons=yes,';
        browserOptions += 'toolbarcolor=#6eaed1,';
        browserOptions += 'closebuttoncolor=#ffc409,';
        browserOptions += 'location=no,';
  
        if (this.platform.is('ios')) {
          var ref = cordova.InAppBrowser.open(this.pdfUrl, '_blank', browserOptions);
        }
        else {
          var ref = cordova.InAppBrowser.open(this.pdfUrl, '_system', browserOptions);
        }
      }
      else {
        window.open(this.pdfUrl);
      }
    }
  }

  async sharePdfReceipt() {
    await Share.share({
      title: 'Kvitto för biljettköp',
      text: 'Länk till PDF-kvitto:',
      url: this.pdfUrl,
      dialogTitle: 'Dela ditt kvitto',
    });
  }

  translateStatus(status: OrderDTO.PaymentStatusEnum): string {
    switch (status) {
      case 'COMPLETE':
        return 'Betald';
      case 'STARTED':
        return 'Pågående';
      default:
        return 'Avbruten';
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
  }

}
